import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import AgentContext from '../../../context/admin/Agent.tsx';
import { useFeltLoading } from '../../../helpers/useFeltLoading.ts';
import { useUser } from '../../../context/UserContext.tsx';
import { formatNumbers } from '../../../helpers/functions.tsx';
import { Users } from '../../../models/User.tsx';
import { agentUnderLabel } from '../../../helpers/userHierarchy.ts';
import { editUser } from '../../../services/admin/UserService.tsx';
import { logUserActivity } from '../../../services/player/LoginService.tsx';
import { logAgentActivity } from '../../../services/admin/AgentActivity.ts';
import {
    DEFAULT_ALLOW_ACCESS_TO_ALL_LIMITS,
    DEFAULT_ALLOW_CREDIT_DEBIT_ADJUSTMENTS,
    DEFAULT_ALLOW_FREE_PLAY,
    DEFAULT_MAX_FREE_PLAY_ODDS,
    DEFAULT_MAX_FREE_PLAY_PAYOUT,
    DEFAULT_MAX_OVERALL_ODDS,
    ensureAgentLimitsOnUser,
    rememberAgentLimits,
} from '../../../services/player/SportService.tsx';
import {
    AGENT_WAGER_LIMIT_FIELDS,
    AgentWagerLimitKey,
    AgentWagerLimitsForm,
    emptyAgentWagerLimits,
    formatOddsDisplay,
    formatOddsInputLive,
    formatWagerDisplay,
    formatWagerInputLive,
    getStatusClass,
    parseOddsInput,
    parseWagerInput,
} from './bettingSetupUtils.ts';
import UnsavedChangesModal from './UnsavedChangesModal.tsx';

type AgentPlayoutSetupProps = {
    onDirtyChange: (dirty: boolean) => void;
    onToast: (type: 'success' | 'error', message: string) => void;
};

function agentLimitPatch(agent: Users): Partial<Users> {
    return {
        agentMaxPayout: agent.agentMaxPayout,
        agentMaxFreePlayPayout: agent.agentMaxFreePlayPayout,
        agentMaxFreePlayOdds: agent.agentMaxFreePlayOdds,
        agentMaxOverallOdds: agent.agentMaxOverallOdds,
        agentRegularBetMaxWager: agent.agentRegularBetMaxWager ?? 0,
        agentParlayMaxWager: agent.agentParlayMaxWager ?? 0,
        agentTeaserMaxWager: agent.agentTeaserMaxWager ?? 0,
        agentLiveMaxWager: agent.agentLiveMaxWager ?? 0,
        agentPropsMaxWager: agent.agentPropsMaxWager ?? 0,
        agentContestMaxWager: agent.agentContestMaxWager ?? 0,
        allowFreePlay: agent.allowFreePlay,
        allowAccessToAllLimits: agent.allowAccessToAllLimits,
        allowCreditDebitAdjustments: agent.allowCreditDebitAdjustments,
    };
}

function AgentPlayoutSetup({ onDirtyChange, onToast }: AgentPlayoutSetupProps) {
    const { user: currentUser, userIpData: userIpDataAdmin, updateUser } = useUser();
    const { allAgents, updateAgentInContext } = useContext(AgentContext);

    const [searchQuery, setSearchQuery] = useState('');
    const hydratedNames = useRef(new Set<string>());
    const [selectedAgentUserName, setSelectedAgentUserName] = useState<string | null>(null);
    const [agentMaxPayout, setAgentMaxPayout] = useState('');
    const [originalAgentMaxPayout, setOriginalAgentMaxPayout] = useState('');
    const [agentMaxFreePlayPayout, setAgentMaxFreePlayPayout] = useState('');
    const [originalAgentMaxFreePlayPayout, setOriginalAgentMaxFreePlayPayout] = useState('');
    const [agentMaxFreePlayOdds, setAgentMaxFreePlayOdds] = useState('');
    const [originalAgentMaxFreePlayOdds, setOriginalAgentMaxFreePlayOdds] = useState('');
    const [agentMaxOverallOdds, setAgentMaxOverallOdds] = useState('');
    const [originalAgentMaxOverallOdds, setOriginalAgentMaxOverallOdds] = useState('');
    const [agentWagerLimits, setAgentWagerLimits] = useState<AgentWagerLimitsForm>(emptyAgentWagerLimits);
    const [originalAgentWagerLimits, setOriginalAgentWagerLimits] = useState<AgentWagerLimitsForm>(emptyAgentWagerLimits);
    const [allowFreePlay, setAllowFreePlay] = useState(DEFAULT_ALLOW_FREE_PLAY);
    const [originalAllowFreePlay, setOriginalAllowFreePlay] = useState(DEFAULT_ALLOW_FREE_PLAY);
    const [allowAccessToAllLimits, setAllowAccessToAllLimits] = useState(DEFAULT_ALLOW_ACCESS_TO_ALL_LIMITS);
    const [originalAllowAccessToAllLimits, setOriginalAllowAccessToAllLimits] = useState(DEFAULT_ALLOW_ACCESS_TO_ALL_LIMITS);
    const [allowCreditDebitAdjustments, setAllowCreditDebitAdjustments] = useState(DEFAULT_ALLOW_CREDIT_DEBIT_ADJUSTMENTS);
    const [originalAllowCreditDebitAdjustments, setOriginalAllowCreditDebitAdjustments] = useState(DEFAULT_ALLOW_CREDIT_DEBIT_ADJUSTMENTS);
    const [isLoading, setIsLoading] = useState(true);
    const showAgentLoading = useFeltLoading(isLoading);
    const [isSaving, setIsSaving] = useState(false);
    const [pendingAgent, setPendingAgent] = useState<Users | null>(null);

    if (!currentUser) {
        throw new Error('User not authenticated');
    }

    const isMasterAgent = currentUser.userType === 'masterAgent';
    const isSelfEdit = currentUser.userType === 'subAgent';

    useEffect(() => {
        let cancelled = false;
        const targets = isSelfEdit ? [currentUser] : allAgents;
        const pending = targets.filter(agent =>
            agent.userName
            && typeof agent.agentMaxPayout !== 'number'
            && !hydratedNames.current.has(agent.userName)
        );

        if (pending.length === 0) {
            setIsLoading(false);
            return;
        }

        pending.forEach(agent => hydratedNames.current.add(agent.userName));
        setIsLoading(true);

        Promise.all(pending.map(agent => ensureAgentLimitsOnUser(agent)))
            .then(hydrated => {
                if (cancelled) return;
                hydrated.forEach(agent => {
                    if (!agent.id || typeof agent.agentMaxPayout !== 'number') return;
                    const patch = agentLimitPatch(agent);
                    rememberAgentLimits(agent);
                    updateAgentInContext(agent.id, patch);
                    if (agent.id === currentUser.id) updateUser(patch);
                });
            })
            .catch(error => {
                console.error(error);
                if (!cancelled) onToast('error', 'Error loading agent payout limits');
            })
            .finally(() => {
                if (!cancelled) setIsLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [allAgents, currentUser, isSelfEdit, onToast, updateAgentInContext, updateUser]);

    const agentsForConfig = useMemo(() => {
        if (isSelfEdit) return [currentUser];

        let agents = [...allAgents];

        if (searchQuery.trim()) {
            const q = searchQuery.trim().toLowerCase();
            agents = agents.filter(
                a =>
                    a.userName.toLowerCase().includes(q) ||
                    (a.name?.toLowerCase().includes(q) ?? false)
            );
        }

        return agents.sort((a, b) => a.userName.localeCompare(b.userName));
    }, [allAgents, currentUser, isSelfEdit, searchQuery]);

    const selectedAgent = useMemo(
        () => agentsForConfig.find(a => a.userName === selectedAgentUserName) ?? null,
        [agentsForConfig, selectedAgentUserName]
    );

    const findAgent = (userName: string): Users | undefined => {
        if (isSelfEdit) return currentUser.userName === userName ? currentUser : undefined;
        return allAgents.find(agent => agent.userName === userName);
    };

    const getAgentMaxPayout = (userName: string): number =>
        findAgent(userName)?.agentMaxPayout ?? 0;

    const getAgentMaxFreePlayPayout = (userName: string): number =>
        findAgent(userName)?.agentMaxFreePlayPayout ?? DEFAULT_MAX_FREE_PLAY_PAYOUT;

    const getAgentMaxFreePlayOdds = (userName: string): number =>
        findAgent(userName)?.agentMaxFreePlayOdds ?? DEFAULT_MAX_FREE_PLAY_ODDS;

    const getAgentMaxOverallOdds = (userName: string): number =>
        findAgent(userName)?.agentMaxOverallOdds ?? DEFAULT_MAX_OVERALL_ODDS;

    const getAgentAllowFreePlay = (userName: string): boolean =>
        findAgent(userName)?.allowFreePlay ?? DEFAULT_ALLOW_FREE_PLAY;

    const getAgentAllowAccessToAllLimits = (userName: string): boolean =>
        findAgent(userName)?.allowAccessToAllLimits ?? DEFAULT_ALLOW_ACCESS_TO_ALL_LIMITS;

    const getAgentAllowCreditDebitAdjustments = (userName: string): boolean =>
        findAgent(userName)?.allowCreditDebitAdjustments ?? DEFAULT_ALLOW_CREDIT_DEBIT_ADJUSTMENTS;

    const getAgentWagerLimits = (userName: string): AgentWagerLimitsForm => {
        const agent = findAgent(userName);
        const form = emptyAgentWagerLimits();
        AGENT_WAGER_LIMIT_FIELDS.forEach(({ key }) => {
            form[key] = formatWagerDisplay(agent?.[key] ?? 0);
        });
        return form;
    };

    const wagerLimitsChanged = AGENT_WAGER_LIMIT_FIELDS.some(
        ({ key }) => agentWagerLimits[key] !== originalAgentWagerLimits[key]
    );

    const hasUnsavedChanges =
        agentMaxPayout !== originalAgentMaxPayout ||
        agentMaxFreePlayPayout !== originalAgentMaxFreePlayPayout ||
        agentMaxFreePlayOdds !== originalAgentMaxFreePlayOdds ||
        agentMaxOverallOdds !== originalAgentMaxOverallOdds ||
        allowFreePlay !== originalAllowFreePlay ||
        allowAccessToAllLimits !== originalAllowAccessToAllLimits ||
        allowCreditDebitAdjustments !== originalAllowCreditDebitAdjustments ||
        wagerLimitsChanged;

    useEffect(() => {
        onDirtyChange(hasUnsavedChanges);
    }, [hasUnsavedChanges, onDirtyChange]);

    const applyAgentDisplays = (userName: string) => {
        const maxDisplay = formatWagerDisplay(getAgentMaxPayout(userName));
        const freePlayDisplay = formatWagerDisplay(getAgentMaxFreePlayPayout(userName));
        const freePlayOddsDisplay = formatOddsDisplay(getAgentMaxFreePlayOdds(userName));
        const overallOddsDisplay = formatOddsDisplay(getAgentMaxOverallOdds(userName));
        const allow = getAgentAllowFreePlay(userName);
        const allowLimits = getAgentAllowAccessToAllLimits(userName);
        const allowAdjustments = getAgentAllowCreditDebitAdjustments(userName);
        const wagerLimits = getAgentWagerLimits(userName);
        setAgentMaxPayout(maxDisplay);
        setOriginalAgentMaxPayout(maxDisplay);
        setAgentMaxFreePlayPayout(freePlayDisplay);
        setOriginalAgentMaxFreePlayPayout(freePlayDisplay);
        setAgentMaxFreePlayOdds(freePlayOddsDisplay);
        setOriginalAgentMaxFreePlayOdds(freePlayOddsDisplay);
        setAgentMaxOverallOdds(overallOddsDisplay);
        setOriginalAgentMaxOverallOdds(overallOddsDisplay);
        setAllowFreePlay(allow);
        setOriginalAllowFreePlay(allow);
        setAllowAccessToAllLimits(allowLimits);
        setOriginalAllowAccessToAllLimits(allowLimits);
        setAllowCreditDebitAdjustments(allowAdjustments);
        setOriginalAllowCreditDebitAdjustments(allowAdjustments);
        setAgentWagerLimits(wagerLimits);
        setOriginalAgentWagerLimits(wagerLimits);
    };

    const selectAgent = (agent: Users) => {
        if (hasUnsavedChanges) {
            setPendingAgent(agent);
            return;
        }

        setSelectedAgentUserName(agent.userName);
        applyAgentDisplays(agent.userName);
    };

    useEffect(() => {
        if (agentsForConfig.length === 0) {
            setSelectedAgentUserName(null);
            setAgentMaxPayout('');
            setOriginalAgentMaxPayout('');
            setAgentMaxFreePlayPayout('');
            setOriginalAgentMaxFreePlayPayout('');
            setAgentMaxFreePlayOdds('');
            setOriginalAgentMaxFreePlayOdds('');
            setAgentMaxOverallOdds('');
            setOriginalAgentMaxOverallOdds('');
            setAllowFreePlay(DEFAULT_ALLOW_FREE_PLAY);
            setOriginalAllowFreePlay(DEFAULT_ALLOW_FREE_PLAY);
            setAllowAccessToAllLimits(DEFAULT_ALLOW_ACCESS_TO_ALL_LIMITS);
            setOriginalAllowAccessToAllLimits(DEFAULT_ALLOW_ACCESS_TO_ALL_LIMITS);
            setAllowCreditDebitAdjustments(DEFAULT_ALLOW_CREDIT_DEBIT_ADJUSTMENTS);
            setOriginalAllowCreditDebitAdjustments(DEFAULT_ALLOW_CREDIT_DEBIT_ADJUSTMENTS);
            const emptyWagers = emptyAgentWagerLimits();
            setAgentWagerLimits(emptyWagers);
            setOriginalAgentWagerLimits(emptyWagers);
            return;
        }

        const resolveDisplays = (userName: string) => ({
            max: formatWagerDisplay(getAgentMaxPayout(userName)),
            freePlay: formatWagerDisplay(getAgentMaxFreePlayPayout(userName)),
            freePlayOdds: formatOddsDisplay(getAgentMaxFreePlayOdds(userName)),
            overallOdds: formatOddsDisplay(getAgentMaxOverallOdds(userName)),
            allow: getAgentAllowFreePlay(userName),
            allowLimits: getAgentAllowAccessToAllLimits(userName),
            allowAdjustments: getAgentAllowCreditDebitAdjustments(userName),
            wagerLimits: getAgentWagerLimits(userName),
        });

        const stillVisible = agentsForConfig.some(a => a.userName === selectedAgentUserName);
        if (!stillVisible) {
            const first = agentsForConfig[0];
            setSelectedAgentUserName(first.userName);
            const displays = resolveDisplays(first.userName);
            setAgentMaxPayout(displays.max);
            setOriginalAgentMaxPayout(displays.max);
            setAgentMaxFreePlayPayout(displays.freePlay);
            setOriginalAgentMaxFreePlayPayout(displays.freePlay);
            setAgentMaxFreePlayOdds(displays.freePlayOdds);
            setOriginalAgentMaxFreePlayOdds(displays.freePlayOdds);
            setAgentMaxOverallOdds(displays.overallOdds);
            setOriginalAgentMaxOverallOdds(displays.overallOdds);
            setAllowFreePlay(displays.allow);
            setOriginalAllowFreePlay(displays.allow);
            setAllowAccessToAllLimits(displays.allowLimits);
            setOriginalAllowAccessToAllLimits(displays.allowLimits);
            setAllowCreditDebitAdjustments(displays.allowAdjustments);
            setOriginalAllowCreditDebitAdjustments(displays.allowAdjustments);
            setAgentWagerLimits(displays.wagerLimits);
            setOriginalAgentWagerLimits(displays.wagerLimits);
        }
    }, [
        agentsForConfig,
        selectedAgentUserName,
        allAgents,
        currentUser,
    ]);

    const selectedLimitKey = selectedAgent
        ? [
            selectedAgent.agentMaxPayout ?? '',
            selectedAgent.agentMaxFreePlayPayout ?? '',
            selectedAgent.agentMaxFreePlayOdds ?? '',
            selectedAgent.agentMaxOverallOdds ?? '',
            selectedAgent.allowFreePlay ?? '',
            selectedAgent.allowAccessToAllLimits ?? '',
            selectedAgent.allowCreditDebitAdjustments ?? '',
            selectedAgent.agentRegularBetMaxWager ?? '',
            selectedAgent.agentParlayMaxWager ?? '',
            selectedAgent.agentTeaserMaxWager ?? '',
            selectedAgent.agentLiveMaxWager ?? '',
            selectedAgent.agentPropsMaxWager ?? '',
            selectedAgent.agentContestMaxWager ?? '',
        ].join('|')
        : '';

    useEffect(() => {
        if (!selectedAgent || hasUnsavedChanges) return;
        applyAgentDisplays(selectedAgent.userName);
    }, [selectedAgent, selectedLimitKey, hasUnsavedChanges]);

    const handleLimitChange = (
        value: string,
        setter: React.Dispatch<React.SetStateAction<string>>
    ) => {
        setter(formatWagerInputLive(value));
    };

    const handleOddsChange = (
        value: string,
        setter: React.Dispatch<React.SetStateAction<string>>
    ) => {
        setter(formatOddsInputLive(value));
    };

    const handleLimitBlur = (
        current: string,
        setter: React.Dispatch<React.SetStateAction<string>>
    ) => {
        const raw = current.trim().toLowerCase();
        if (raw === '' || raw === 'unlimited' || raw === '0') {
            setter('Unlimited');
            return;
        }
        const parsed = parseWagerInput(current);
        if (parsed > 0) {
            setter(formatNumbers(parsed));
        }
    };

    const handleOddsBlur = (
        current: string,
        setter: React.Dispatch<React.SetStateAction<string>>
    ) => {
        const raw = current.trim().toLowerCase();
        if (raw === '' || raw === 'unlimited' || raw === '0' || raw === '+0') {
            setter('Unlimited');
            return;
        }
        const parsed = parseOddsInput(current);
        if (parsed > 0) {
            setter(formatOddsDisplay(parsed));
        }
    };

    const handleReset = () => {
        setAgentMaxPayout(originalAgentMaxPayout);
        setAgentMaxFreePlayPayout(originalAgentMaxFreePlayPayout);
        setAgentMaxFreePlayOdds(originalAgentMaxFreePlayOdds);
        setAgentMaxOverallOdds(originalAgentMaxOverallOdds);
        setAllowFreePlay(originalAllowFreePlay);
        setAllowAccessToAllLimits(originalAllowAccessToAllLimits);
        setAllowCreditDebitAdjustments(originalAllowCreditDebitAdjustments);
        setAgentWagerLimits({ ...originalAgentWagerLimits });
    };

    const handleSave = async () => {
        if (!selectedAgent) return;
        if (isSelfEdit && getAgentAllowAccessToAllLimits(currentUser.userName) !== true) {
            onToast('error', 'You do not have access to edit these limits');
            return;
        }

        setIsSaving(true);
        try {
            const newMaxPayout = parseWagerInput(agentMaxPayout);
            const newMaxFreePlayPayout = parseWagerInput(agentMaxFreePlayPayout);
            const newMaxFreePlayOdds = parseOddsInput(agentMaxFreePlayOdds);
            const newMaxOverallOdds = parseOddsInput(agentMaxOverallOdds);
            const newAllowFreePlay = isMasterAgent ? allowFreePlay : originalAllowFreePlay;
            const newAllowAccessToAllLimits = isMasterAgent
                ? allowAccessToAllLimits
                : originalAllowAccessToAllLimits;
            const newAllowCreditDebitAdjustments = isMasterAgent
                ? allowCreditDebitAdjustments
                : originalAllowCreditDebitAdjustments;
            const oldMaxPayout = getAgentMaxPayout(selectedAgent.userName);
            const oldMaxFreePlayPayout = getAgentMaxFreePlayPayout(selectedAgent.userName);
            const oldMaxFreePlayOdds = getAgentMaxFreePlayOdds(selectedAgent.userName);
            const oldMaxOverallOdds = getAgentMaxOverallOdds(selectedAgent.userName);
            const oldAllowFreePlay = getAgentAllowFreePlay(selectedAgent.userName);
            const oldAllowAccessToAllLimits = getAgentAllowAccessToAllLimits(selectedAgent.userName);
            const oldAllowCreditDebitAdjustments = getAgentAllowCreditDebitAdjustments(selectedAgent.userName);
            const newWagerLimits = Object.fromEntries(
                AGENT_WAGER_LIMIT_FIELDS.map(({ key }) => [key, parseWagerInput(agentWagerLimits[key])])
            ) as Record<AgentWagerLimitKey, number>;
            const oldWagerLimits = getAgentWagerLimits(selectedAgent.userName);

            if (
                oldMaxPayout === newMaxPayout &&
                oldMaxFreePlayPayout === newMaxFreePlayPayout &&
                oldMaxFreePlayOdds === newMaxFreePlayOdds &&
                oldMaxOverallOdds === newMaxOverallOdds &&
                oldAllowFreePlay === newAllowFreePlay &&
                oldAllowAccessToAllLimits === newAllowAccessToAllLimits &&
                oldAllowCreditDebitAdjustments === newAllowCreditDebitAdjustments &&
                AGENT_WAGER_LIMIT_FIELDS.every(
                    ({ key }) => parseWagerInput(oldWagerLimits[key]) === newWagerLimits[key]
                )
            ) {
                onToast('success', 'No changes to save');
                return;
            }

            if (!selectedAgent.id) {
                onToast('error', 'Error saving agent payout');
                return;
            }

            const patch: Partial<Users> = {
                agentMaxPayout: newMaxPayout,
                agentMaxFreePlayPayout: newMaxFreePlayPayout,
                agentMaxFreePlayOdds: newMaxFreePlayOdds,
                agentMaxOverallOdds: newMaxOverallOdds,
                allowFreePlay: newAllowFreePlay,
                allowAccessToAllLimits: newAllowAccessToAllLimits,
                allowCreditDebitAdjustments: newAllowCreditDebitAdjustments,
                ...newWagerLimits,
            };

            await editUser(selectedAgent.id, patch);
            const saved = { ...selectedAgent, ...patch };
            rememberAgentLimits(saved);
            updateAgentInContext(selectedAgent.id, patch);
            if (selectedAgent.id === currentUser.id) updateUser(patch);

            if (currentUser?.userName) {
                void logAgentActivity({
                    userName: currentUser.userName,
                    parent: currentUser.parent,
                    activity: 'Change agent limits',
                    customer: selectedAgent.userName,
                });
                const fmt = (n: number) => (n > 0 ? formatNumbers(n) : 'Unlimited');
                const fmtOdds = (n: number) => (n > 0 ? `+${formatNumbers(n)}` : 'Unlimited');
                await logUserActivity(
                    currentUser.userName,
                    `Betting Setup Agent Payout: master=${currentUser.userName} agent=${selectedAgent.userName} | maxPayout: ${fmt(oldMaxPayout)} → ${fmt(newMaxPayout)} | maxFreePlayPayout: ${fmt(oldMaxFreePlayPayout)} → ${fmt(newMaxFreePlayPayout)} | maxFreePlayOdds: ${fmtOdds(oldMaxFreePlayOdds)} → ${fmtOdds(newMaxFreePlayOdds)} | maxOverallOdds: ${fmtOdds(oldMaxOverallOdds)} → ${fmtOdds(newMaxOverallOdds)} | ${AGENT_WAGER_LIMIT_FIELDS.map(({ key }) => `${key}: ${oldWagerLimits[key]} → ${fmt(newWagerLimits[key])}`).join(' | ')} | allowFreePlay: ${oldAllowFreePlay ? 'Yes' : 'No'} → ${newAllowFreePlay ? 'Yes' : 'No'} | allowAccessToAllLimits: ${oldAllowAccessToAllLimits ? 'Yes' : 'No'} → ${newAllowAccessToAllLimits ? 'Yes' : 'No'} | allowCreditDebitAdjustments: ${oldAllowCreditDebitAdjustments ? 'Yes' : 'No'} → ${newAllowCreditDebitAdjustments ? 'Yes' : 'No'}`,
                    userIpDataAdmin
                );
            }

            const maxDisplay = formatWagerDisplay(newMaxPayout);
            const freePlayDisplay = formatWagerDisplay(newMaxFreePlayPayout);
            const freePlayOddsDisplay = formatOddsDisplay(newMaxFreePlayOdds);
            const overallOddsDisplay = formatOddsDisplay(newMaxOverallOdds);
            setAgentMaxPayout(maxDisplay);
            setOriginalAgentMaxPayout(maxDisplay);
            setAgentMaxFreePlayPayout(freePlayDisplay);
            setOriginalAgentMaxFreePlayPayout(freePlayDisplay);
            setAgentMaxFreePlayOdds(freePlayOddsDisplay);
            setOriginalAgentMaxFreePlayOdds(freePlayOddsDisplay);
            setAgentMaxOverallOdds(overallOddsDisplay);
            setOriginalAgentMaxOverallOdds(overallOddsDisplay);
            setAllowFreePlay(newAllowFreePlay);
            setOriginalAllowFreePlay(newAllowFreePlay);
            setAllowAccessToAllLimits(newAllowAccessToAllLimits);
            setOriginalAllowAccessToAllLimits(newAllowAccessToAllLimits);
            setAllowCreditDebitAdjustments(newAllowCreditDebitAdjustments);
            setOriginalAllowCreditDebitAdjustments(newAllowCreditDebitAdjustments);
            const savedWagers = emptyAgentWagerLimits();
            AGENT_WAGER_LIMIT_FIELDS.forEach(({ key }) => {
                savedWagers[key] = formatWagerDisplay(newWagerLimits[key]);
            });
            setAgentWagerLimits(savedWagers);
            setOriginalAgentWagerLimits(savedWagers);
            onToast('success', `Max Payout updated for ${selectedAgent.userName}`);
        } catch (error) {
            console.error(error);
            onToast('error', 'Error saving agentPayout');
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <>
            {!isSelfEdit && (
                <div className="betting-setup-toolbar betting-setup-toolbar-inline">
                    <input
                        type="text"
                        className="betting-setup-search"
                        placeholder="Search agents..."
                        value={searchQuery}
                        onChange={e => setSearchQuery(e.target.value)}
                        autoComplete="off"
                    />
                </div>
            )}

            <div className={`betting-setup-layout${isSelfEdit ? ' betting-setup-layout-self' : ''}`}>
                {!isSelfEdit && (
                <div className="betting-setup-players-panel">
                    <div className="betting-setup-panel-header">
                        <span>Agents</span>
                        <span className="betting-setup-player-count">{agentsForConfig.length}</span>
                    </div>
                    <div className="betting-setup-players-list">
                        {showAgentLoading ? (
                            Array.from({ length: 8 }, (_, index) => (
                                <div key={index} className="betting-setup-player-item" aria-hidden="true">
                                    <span className="rect-placeholder admin-sk" style={{ width: 120, height: 14 }} />
                                </div>
                            ))
                        ) : agentsForConfig.length === 0 ? (
                            <div className="betting-setup-empty">No agents found</div>
                        ) : (
                            agentsForConfig.map(agent => (
                                <div key={agent.id ?? agent.userName} role="button" tabIndex={0}
                                    className={`betting-setup-player-item ${selectedAgentUserName === agent.userName ? 'active' : ''}`}
                                    onClick={() => selectAgent(agent)} onKeyDown={e => e.key === 'Enter' && selectAgent(agent)} >
                                    <div className="betting-setup-player-info">
                                        <span className="betting-setup-player-name">{agent.userName}</span>
                                        {agent.name && (
                                            <span className="betting-setup-player-agent">{agent.name}</span>
                                        )}
                                        {agentUnderLabel(agent, currentUser, allAgents) && (
                                            <span className="betting-setup-player-agent">
                                                {agentUnderLabel(agent, currentUser, allAgents)}
                                            </span>
                                        )}
                                        <span className="betting-setup-player-agent">
                                            Max Payout: {formatWagerDisplay(getAgentMaxPayout(agent.userName))}
                                        </span>
                                        <span className="betting-setup-player-agent">
                                            Max Free Play: {formatWagerDisplay(getAgentMaxFreePlayPayout(agent.userName))}
                                        </span>
                                        <span className="betting-setup-player-agent">
                                            Max FP Odds: {formatOddsDisplay(getAgentMaxFreePlayOdds(agent.userName))}
                                        </span>
                                        <span className="betting-setup-player-agent">
                                            Max Odds: {formatOddsDisplay(getAgentMaxOverallOdds(agent.userName))}
                                        </span>
                                    </div>
                                    <span className={`betting-setup-player-status ${getStatusClass(agent.status)}`}>
                                        {agent.status}
                                    </span>
                                </div>
                            ))
                        )}
                    </div>
                </div>
                )}

                <div className="betting-setup-editor-panel">
                    <div className="betting-setup-panel-header">
                        <span>Agent Max Payout</span>
                    </div>

                    {!selectedAgent ? (
                        <div className="betting-setup-empty">Select an agent to edit max Payout</div>
                    ) : (
                        <div className="betting-setup-editor-content">
                            <div className="betting-setup-selected-user">
                                <div>
                                    <div className="betting-setup-selected-name">{selectedAgent.userName}</div>
                                    <div className="betting-setup-selected-meta">
                                        {selectedAgent.name && `${selectedAgent.name} · `}
                                        Master: {selectedAgent.parent ?? currentUser.userName}
                                    </div>
                                </div>
                                <span className={`betting-setup-player-status ${getStatusClass(selectedAgent.status)}`}>
                                    {selectedAgent.status}
                                </span>
                            </div>

                            <p className="betting-setup-hint">
                                Enter 0 or &quot;Unlimited&quot; for no maximum on each limit.
                                Odds fields use American format (e.g. +905).
                            </p>

                            <div className="betting-setup-limits-grid">
                                <div className="betting-setup-limit-card">
                                    <div className="betting-setup-limit-label">
                                        <span className="betting-setup-limit-icon">💰</span>
                                        Max Payout
                                    </div>
                                    <div className="betting-setup-limit-desc">
                                        Maximum payout allowed for this agent
                                    </div>
                                    <div className="betting-setup-limit-input-wrap">
                                        <span className="betting-setup-limit-prefix">$</span>
                                        <input
                                            type="text"
                                            className="betting-setup-limit-input"
                                            value={agentMaxPayout}
                                            onChange={e => handleLimitChange(e.target.value, setAgentMaxPayout)}
                                            onBlur={() => handleLimitBlur(agentMaxPayout, setAgentMaxPayout)}
                                            autoComplete="off"
                                            disabled={isSaving}
                                        />
                                    </div>
                                </div>

                                <div className="betting-setup-limit-card">
                                    <div className="betting-setup-limit-label">
                                        <span className="betting-setup-limit-icon">🎁</span>
                                        Max Free Play Win
                                    </div>
                                    <div className="betting-setup-limit-desc">
                                        Maximum win allowed when using Free Play
                                    </div>
                                    <div className="betting-setup-limit-input-wrap">
                                        <span className="betting-setup-limit-prefix">$</span>
                                        <input
                                            type="text"
                                            className="betting-setup-limit-input"
                                            value={agentMaxFreePlayPayout}
                                            onChange={e =>
                                                handleLimitChange(e.target.value, setAgentMaxFreePlayPayout)
                                            }
                                            onBlur={() =>
                                                handleLimitBlur(agentMaxFreePlayPayout, setAgentMaxFreePlayPayout)
                                            }
                                            autoComplete="off"
                                            disabled={isSaving}
                                        />
                                    </div>
                                </div>

                                <div className="betting-setup-limit-card">
                                    <div className="betting-setup-limit-label">
                                        <span className="betting-setup-limit-icon">🎯</span>
                                        Max Free Play Odds
                                    </div>
                                    <div className="betting-setup-limit-desc">
                                        Highest American odds allowed on Free Play (e.g. +905)
                                    </div>
                                    <div className="betting-setup-limit-input-wrap">
                                        <input
                                            type="text"
                                            className="betting-setup-limit-input"
                                            value={agentMaxFreePlayOdds}
                                            onChange={e =>
                                                handleOddsChange(e.target.value, setAgentMaxFreePlayOdds)
                                            }
                                            onBlur={() =>
                                                handleOddsBlur(agentMaxFreePlayOdds, setAgentMaxFreePlayOdds)
                                            }
                                            placeholder="+905"
                                            autoComplete="off"
                                            disabled={isSaving}
                                        />
                                    </div>
                                </div>

                                <div className="betting-setup-limit-card">
                                    <div className="betting-setup-limit-label">
                                        <span className="betting-setup-limit-icon">📈</span>
                                        Max Overall Odds
                                    </div>
                                    <div className="betting-setup-limit-desc">
                                        Highest American odds allowed overall (e.g. +2000)
                                    </div>
                                    <div className="betting-setup-limit-input-wrap">
                                        <input
                                            type="text"
                                            className="betting-setup-limit-input"
                                            value={agentMaxOverallOdds}
                                            onChange={e =>
                                                handleOddsChange(e.target.value, setAgentMaxOverallOdds)
                                            }
                                            onBlur={() =>
                                                handleOddsBlur(agentMaxOverallOdds, setAgentMaxOverallOdds)
                                            }
                                            placeholder="+2000"
                                            autoComplete="off"
                                            disabled={isSaving}
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="betting-setup-section-label">Wager limits</div>
                            <div className="betting-setup-limits-grid">
                                {AGENT_WAGER_LIMIT_FIELDS.map(({ key, label, icon, description }) => (
                                    <div key={key} className="betting-setup-limit-card">
                                        <div className="betting-setup-limit-label">
                                            <span className="betting-setup-limit-icon">{icon}</span>
                                            {label}
                                        </div>
                                        <div className="betting-setup-limit-desc">{description}</div>
                                        <div className="betting-setup-limit-input-wrap">
                                            <span className="betting-setup-limit-prefix">$</span>
                                            <input
                                                type="text"
                                                className="betting-setup-limit-input"
                                                value={agentWagerLimits[key]}
                                                onChange={e =>
                                                    setAgentWagerLimits(prev => ({
                                                        ...prev,
                                                        [key]: formatWagerInputLive(e.target.value),
                                                    }))
                                                }
                                                onBlur={() => {
                                                    const current = agentWagerLimits[key];
                                                    const raw = current.trim().toLowerCase();
                                                    const next =
                                                        raw === '' || raw === 'unlimited' || raw === '0'
                                                            ? 'Unlimited'
                                                            : parseWagerInput(current) > 0
                                                                ? formatNumbers(parseWagerInput(current))
                                                                : current;
                                                    setAgentWagerLimits(prev => ({ ...prev, [key]: next }));
                                                }}
                                                autoComplete="off"
                                                disabled={isSaving}
                                            />
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {isMasterAgent && (
                            <div className="betting-setup-toggles">
                            <label className={`betting-setup-freeplay-toggle ${allowFreePlay ? 'active' : ''}`}>
                                <input
                                    type="checkbox"
                                    checked={allowFreePlay}
                                    onChange={e => setAllowFreePlay(e.target.checked)}
                                    disabled={isSaving}
                                />
                                <span className="betting-setup-freeplay-toggle-text">
                                    <span className="betting-setup-freeplay-toggle-title">
                                        Allow agent to give Free Play
                                    </span>
                                    <span className="betting-setup-freeplay-toggle-desc">
                                        When disabled, this agent cannot give Free Play to their players
                                    </span>
                                </span>
                            </label>

                            <label className={`betting-setup-freeplay-toggle ${allowCreditDebitAdjustments ? 'active' : ''}`}>
                                <input
                                    type="checkbox"
                                    checked={allowCreditDebitAdjustments}
                                    onChange={e => setAllowCreditDebitAdjustments(e.target.checked)}
                                    disabled={isSaving}
                                />
                                <span className="betting-setup-freeplay-toggle-text">
                                    <span className="betting-setup-freeplay-toggle-title">
                                        Allow balance changes
                                    </span>
                                    <span className="betting-setup-freeplay-toggle-desc">
                                        When disabled, this agent cannot make deposits, withdrawals, credit or debit adjustments, or zero out a balance
                                    </span>
                                </span>
                            </label>

                            <label className={`betting-setup-freeplay-toggle ${allowAccessToAllLimits ? 'active' : ''}`}>
                                <input
                                    type="checkbox"
                                    checked={allowAccessToAllLimits}
                                    onChange={e => setAllowAccessToAllLimits(e.target.checked)}
                                    disabled={isSaving}
                                />
                                <span className="betting-setup-freeplay-toggle-text">
                                    <span className="betting-setup-freeplay-toggle-title">
                                        Allow agent access to all limits
                                    </span>
                                    <span className="betting-setup-freeplay-toggle-desc">
                                        When enabled, this agent can change these payout and odds limits from their own Betting Setup
                                    </span>
                                </span>
                            </label>
                            </div>
                            )}

                            <div className="betting-setup-actions">
                                {hasUnsavedChanges && (
                                    <span className="betting-setup-unsaved">Unsaved changes</span>
                                )}
                                <button
                                    type="button"
                                    className="betting-setup-btn betting-setup-btn-reset"
                                    onClick={handleReset}
                                    disabled={!hasUnsavedChanges || isSaving}
                                >
                                    Reset
                                </button>
                                <button
                                    type="button"
                                    className="betting-setup-btn betting-setup-btn-save"
                                    onClick={handleSave}
                                    disabled={!hasUnsavedChanges || isSaving}
                                >
                                    {isSaving ? 'Saving...' : 'Save Changes'}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            <UnsavedChangesModal
                show={pendingAgent !== null}
                onKeepEditing={() => setPendingAgent(null)}
                onDiscard={() => {
                    if (!pendingAgent) return;
                    setSelectedAgentUserName(pendingAgent.userName);
                    applyAgentDisplays(pendingAgent.userName);
                    setPendingAgent(null);
                }}
            />
        </>
    );
}

export default AgentPlayoutSetup;
